
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Layers, 
  Search, 
  Settings, 
  Music, 
  Globe, 
  Zap, 
  Play, 
  Pause, 
  RotateCcw, 
  X,
  ChevronRight,
  Cpu,
  Palette,
  Wrench,
  LucideIcon,
  Smartphone,
  Star,
  Settings2,
  Database,
  Save,
  Grid,
  Video,
  Lock as LucideLock,
  Shield,
  RefreshCw,
  WifiOff,
  Moon,
  Sun,
  Pin,
  PinOff
} from 'lucide-react';
import { SimulationConfig } from '@/types';
import { ModeSelectorSection } from './ModeSelectorSection';
import { SpawnerSettingsSection } from './SpawnerSettingsSection';
import { DrawerSettingsSection } from './DrawerSettingsSection';
import { EscapeSettingsSection } from './EscapeSettingsSection';
import { RhythmicModeSection } from './RhythmicModeSection';
import { SpaceSpiralSettingsSection } from './SpaceSpiralSettingsSection';
import { PhysicsSettingsSection } from './PhysicsSettingsSection';
import { BackgroundSettingsSection } from './BackgroundSettingsSection';
import { HUDSettingsSection } from './HUDSettingsSection';
import { CameraSettingsSection } from './CameraSettingsSection';
import { VisualsTab } from './VisualsTab';
import { AudioStudioTab } from './AudioStudioTab';
import { StudioRecorderTab } from './StudioRecorderTab';
import { StorageManager } from './StorageManager';
import { OptimizationSettingsSection } from './OptimizationSettingsSection';
import { BallPlacementSection } from './BallPlacementSection';
import { OutsideColliderSection } from './OutsideColliderSection';
import { PhysicsTab } from './PhysicsTab';
import { CloudStorageSection } from '@/components/settings/CloudStorageSection';
import { SettingsSearchContext, ConfigContext } from './SettingsContext';
import { useSettingLocks } from '@/hooks/useSettingLocks';
import { usePayment } from '@/contexts/PaymentContext';
import { useAuth } from '@/context/auth-context';
import { LockOverlay } from './LockOverlay';
import { UpgradeTab } from './UpgradeTab';

interface SettingsPanelProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  showUI: boolean;
  setShowUI: (s: boolean) => void;
  isPlaying: boolean;
  setIsPlaying: (p: boolean) => void;
  onReset: () => void;
  onSave: () => void;
  saveStatus: string;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isAnalyzing: boolean;
  analysisProgress?: number;
  onBallSoundUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isUploadingBallSound: boolean;
  cloudSaves: any[];
  setCloudSaves: React.Dispatch<React.SetStateAction<any[]>>;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  bgCanvasRef: React.RefObject<HTMLCanvasElement | null>;
  webglCanvasRef: React.RefObject<HTMLCanvasElement | null>;
  sim2dCanvasRef: React.RefObject<HTMLCanvasElement | null>;
  sidebarWidth: number;
  setSidebarWidth: (w: number) => void;
  ballPoolRef?: React.MutableRefObject<any>;
  trailCirclePoolRef?: React.MutableRefObject<any>;
  collisionEventPoolRef?: React.MutableRefObject<any>;
  blastEventPoolRef?: React.MutableRefObject<any>;
  workerRef?: React.MutableRefObject<Worker | null>;
}

type TabType = 'SIM' | 'PHYSICS' | 'VISUALS' | 'AUDIO' | 'REC' | 'DATA' | 'PLAN';
type SimSubTab = 'ENGINE' | 'WORLD' | 'OPTIMIZATION';

export const SettingsPanel: React.FC<SettingsPanelProps> = ({
  config, setConfig, showUI, setShowUI, isPlaying, setIsPlaying, onReset, onSave, saveStatus, onFileUpload, isAnalyzing, analysisProgress = 0, onBallSoundUpload, isUploadingBallSound,
  cloudSaves, setCloudSaves, canvasRef, bgCanvasRef, webglCanvasRef, sim2dCanvasRef,
  sidebarWidth, setSidebarWidth,
  ballPoolRef, trailCirclePoolRef, collisionEventPoolRef, blastEventPoolRef, workerRef
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('SIM');
  const [activeSimSubTab, setActiveSimSubTab] = useState<SimSubTab>('ENGINE');
  const [searchQuery, setSearchQuery] = useState('');
  const [isResizing, setIsResizing] = useState(false);
  
  const { isSettingLocked, getLockInfo, isOnline, hasFetched } = useSettingLocks(config.mode);
  const { subscription } = usePayment();
  const { user } = useAuth();

  // Sync pinned state with UI visibility
  useEffect(() => {
    if (config.pinnedSidebar && !showUI) {
      setShowUI(true);
    }
  }, [config.pinnedSidebar, showUI, setShowUI]);

  const isStudioLocked = !isOnline || !hasFetched;

  // Mode-based visibility logic
  const shouldShowSection = (sectionModes: string[]) => {
      if (!sectionModes || sectionModes.length === 0 || sectionModes.includes('GLOBAL')) return true;
      const currentMode = config.mode;
      
      const hasRhythmic = config.enableRhythmic || currentMode === 'RHYTHMIC' || currentMode === 'RHYTHMIC_ESCAPE';
      const hasEscape = config.enableEscape || currentMode === 'ESCAPE' || currentMode === 'RHYTHMIC_ESCAPE';
      const hasTrails = config.enableTrails || config.trailEnabled;
      const hasSpawner = config.enableSpawner || currentMode === 'SPAWNER';

      if (sectionModes.includes(currentMode)) return true;
      if (sectionModes.includes('RHYTHMIC') && hasRhythmic) return true;
      if (sectionModes.includes('ESCAPE') && hasEscape) return true;
      if (sectionModes.includes('TRAILS') && hasTrails) return true;
      if (sectionModes.includes('SPAWNER') && hasSpawner) return true;

      return false;
  };

  const tabs: { id: TabType, label: string, icon: LucideIcon }[] = [
    { id: 'SIM', label: 'Engine', icon: Cpu },
    { id: 'PHYSICS', label: 'Physics', icon: Zap },
    { id: 'VISUALS', label: 'Art', icon: Palette },
    { id: 'AUDIO', label: 'Audio', icon: Music },
    { id: 'REC', label: 'Video', icon: Video },
    { id: 'DATA', label: 'Cloud', icon: Database },
  ];

  const simTabs: { id: SimSubTab, label: string, icon: LucideIcon }[] = [
      { id: 'ENGINE', label: 'Core', icon: Settings2 },
      { id: 'WORLD', label: 'Space', icon: Globe },
      { id: 'OPTIMIZATION', label: 'Perf', icon: Zap },
  ];

  const handleMouseDown = useCallback(() => {
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = window.innerWidth - e.clientX;
      setSidebarWidth(Math.max(400, Math.min(newWidth, 900)));
    };
    const handleMouseUp = () => setIsResizing(false);

    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  return (
    <ConfigContext.Provider value={config}>
    <SettingsSearchContext.Provider value={searchQuery}>
    <div className="absolute top-0 right-0 h-full flex items-center z-[1000] pointer-events-none w-full justify-end">
      <motion.div 
        initial={false}
        animate={{ 
          x: showUI ? 0 : sidebarWidth,
          width: sidebarWidth,
          opacity: showUI ? 1 : 0
        }}
        transition={{ type: 'spring', damping: 30, stiffness: 250 }}
        className={`flex h-full shadow-2xl border-l pointer-events-auto overflow-hidden ${config.darkMode ? 'bg-zinc-950/98 text-white border-white/10' : 'bg-white/98 text-zinc-900 border-zinc-950/10'} backdrop-blur-3xl relative`}
      >
        <div 
            onMouseDown={handleMouseDown}
            className="absolute left-0 top-0 w-1 h-full cursor-ew-resize hover:bg-blue-500/50 transition-colors z-[1001]"
        />

        {/* Categories Sidebar (Left inside the panel) */}
        <div className={`w-20 sm:w-24 h-full border-r flex flex-col items-center py-6 gap-2 ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-zinc-50 border-zinc-100'}`}>
             <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all mb-8 shadow-sm border ${config.darkMode ? 'bg-cyan-500/10 border-cyan-500/20 text-cyan-400' : 'bg-white border-zinc-100 text-cyan-600'}`}>
                <Layers className="w-6 h-6" />
            </div>

            <div className="flex-1 flex flex-col gap-3 w-full px-2 overflow-y-auto custom-scrollbar">
                {tabs.map(t => {
                    const active = activeTab === t.id;
                    const Icon = t.icon;
                    return (
                        <button 
                            key={t.id}
                            onClick={() => setActiveTab(t.id)}
                            className={`w-full py-4 rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all relative border ${active ? (config.darkMode ? 'border-cyan-400/30 bg-cyan-400/10 text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.1)]' : 'border-cyan-600/30 bg-white text-cyan-600 shadow-md') : 'border-transparent opacity-20 hover:opacity-100 hover:bg-zinc-500/5 text-zinc-500'}`}
                        >
                            <Icon className="w-3.5 h-3.5 shrink-0" />
                            <span className="text-[7.5px] font-medium uppercase tracking-[0.2em] leading-none">{t.label}</span>
                        </button>
                    );
                })}
            </div>

            <div className="mt-auto p-2 border-t border-zinc-950/5 flex flex-col gap-2 w-full">
                <button 
                  onClick={() => setActiveTab('PLAN')}
                  className={`w-full py-4 rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all border ${activeTab === 'PLAN' ? (config.darkMode ? 'bg-cyan-400/10 border-cyan-400/30 text-cyan-400 shadow-[0_0_20px_rgba(6,182,212,0.1)]' : 'bg-white border-cyan-600/30 text-cyan-600 shadow-md') : (config.darkMode ? 'bg-white/5 border-white/5 text-white/40 hover:bg-white/10' : 'bg-white border-zinc-100 text-zinc-400 hover:bg-zinc-50 shadow-sm')}`}
                >
                  <Star className={`w-3.5 h-3.5 shrink-0 ${activeTab === 'PLAN' ? 'text-amber-500 fill-amber-500' : 'opacity-40'}`} />
                  <span className="text-[7.5px] font-medium uppercase tracking-[0.2em] leading-none">Upgrade</span>
                </button>

                <button 
                    onClick={() => setShowUI(false)}
                    className="w-full py-4 rounded-2xl flex items-center justify-center hover:bg-red-500/10 text-zinc-400 hover:text-red-500 transition-all active:scale-90"
                >
                    <X className="w-6 h-6" />
                </button>
            </div>
        </div>

        {/* Main Content (Right inside the panel) */}
        <div className="flex-1 flex flex-col h-full overflow-hidden">
            {/* Header Content */}
            <div className="p-6 border-b border-zinc-500/5 flex items-center justify-between">
                <div className="flex flex-col">
                    <h1 className="text-[9px] font-medium uppercase tracking-[0.4em] opacity-30">Creator Studio</h1>
                    <div className="flex items-center gap-2 mt-2">
                        <div className={`w-1.5 h-1.5 rounded-full ${isPlaying ? 'bg-cyan-500 shadow-[0_0_8px_rgba(6,182,212,0.5)] animate-pulse' : 'bg-zinc-300'}`} />
                        <span className="text-[8px] font-medium opacity-40 uppercase tracking-[0.15em]">{isPlaying ? 'Simulation Active' : 'v4.0.1 Protocol'}</span>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {/* Dark Mode & Pin Toggle */}
                    <div className={`flex items-center p-1 rounded-2xl border ${config.darkMode ? 'bg-white/5 border-white/5' : 'bg-zinc-50 border-zinc-100 shadow-inner'}`}>
                        <button 
                            onClick={() => setConfig(prev => {
                                const isDark = !prev.darkMode;
                                return {
                                    ...prev,
                                    darkMode: isDark,
                                    innerColor: isDark ? '#000000' : '#ffffff',
                                    outerColor: isDark ? '#000000' : '#ffffff',
                                    titleColor: isDark ? '#ffffff' : '#171717',
                                    footerTextColor: isDark ? '#ffffff' : '#171717',
                                    backgroundPrimaryColor: isDark ? '#000000' : '#ffffff',
                                    backgroundSecondaryColor: isDark ? '#000000' : '#ffffff',
                                    backgroundTertiaryColor: isDark ? '#000000' : '#ffffff',
                                    ringCounterColor: isDark ? '#ffffff' : '#1a1a1a',
                                    timerColor: isDark ? '#ffffff' : '#1a1a1a',
                                    ballSizeCounterColor: isDark ? '#ffffff' : '#1a1a1a',
                                    bounceCounterColor: isDark ? '#ffffff' : '#1a1a1a',
                                    borderColor: isDark ? '#ffffff' : '#1a1a1a'
                                };
                            })}
                            className={`w-9 h-9 rounded-xl transition-all flex items-center justify-center ${config.darkMode ? 'bg-cyan-500/10 text-cyan-400' : 'text-zinc-400 hover:bg-zinc-200/50'}`}
                            title={config.darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                        >
                            {config.darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        </button>
                        <button 
                            onClick={() => setConfig(prev => ({ ...prev, pinnedSidebar: !prev.pinnedSidebar }))}
                            className={`w-9 h-9 rounded-xl transition-all flex items-center justify-center ${config.pinnedSidebar ? (config.darkMode ? 'bg-cyan-500/10 text-cyan-400' : 'bg-white border border-cyan-600/20 text-cyan-600 shadow-sm') : 'text-zinc-300 hover:bg-zinc-200/50'}`}
                            title={config.pinnedSidebar ? 'Unpin Sidebar' : 'Pin Sidebar'}
                        >
                            {config.pinnedSidebar ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                        </button>
                    </div>

                    {/* Play/Pause in Header as well for convenience */}
                    <div className={`flex items-center p-1 rounded-2xl border ${config.darkMode ? 'bg-white/5 border-white/5' : 'bg-zinc-50 border-zinc-100 shadow-inner'}`}>
                        <button 
                            onClick={() => setIsPlaying(!isPlaying)}
                            className={`w-10 h-10 rounded-xl transition-all flex items-center justify-center ${isPlaying ? (config.darkMode ? 'bg-cyan-500/10 text-cyan-400' : 'bg-white border border-cyan-600/20 text-cyan-600 shadow-sm') : 'text-zinc-300 hover:text-zinc-600 hover:bg-white/10'}`}
                        >
                            {isPlaying ? <Pause className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current ml-0.5" />}
                        </button>
                        <button 
                            onClick={onReset}
                            className="w-10 h-10 rounded-xl text-zinc-300 hover:text-zinc-600 transition-all hover:rotate-180 flex items-center justify-center hover:bg-white/10"
                        >
                            <RotateCcw className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Sub-Tabs Picker (for Engine Section) */}
            {activeTab === 'SIM' && (
                <div className="px-6 pt-4">
                    <div className={`flex gap-1 p-1 rounded-2xl ${config.darkMode ? 'bg-white/5' : 'bg-zinc-100'}`}>
                        {simTabs.map(st => {
                            const active = activeSimSubTab === st.id;
                            const Icon = st.icon;
                            return (
                                <button 
                                    key={st.id}
                                    onClick={() => setActiveSimSubTab(st.id)}
                                    className={`flex items-center justify-center gap-2 py-2 flex-1 rounded-xl transition-all relative border-transparent border ${active ? (config.darkMode ? 'border-cyan-400/30 bg-cyan-400/10 text-cyan-400 shadow-[0_0_10px_rgba(6,182,212,0.1)]' : 'border-cyan-600/30 bg-white text-cyan-600 shadow-sm') : 'opacity-30 hover:opacity-60 text-zinc-500'}`}
                                >
                                    <Icon className="w-3 h-3" />
                                    <span className="text-[9px] font-medium uppercase tracking-widest">{st.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Search Input (Global to content) */}
            <div className="px-6 py-4">
                <div className={`flex items-center gap-3 px-4 py-2 rounded-xl border transition-all ${config.darkMode ? 'bg-white/5 border-white/5' : 'bg-zinc-50 border-zinc-100 focus-within:bg-white focus-within:shadow-sm'}`}>
                    <Search className="w-3 h-3 opacity-20" />
                    <input 
                        type="text" 
                        placeholder={`Search ${activeTab.toLowerCase()} parameters...`}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="bg-transparent border-none outline-none text-[10px] font-medium w-full uppercase tracking-wider placeholder:opacity-40"
                    />
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar px-6 pb-20 relative">
                {/* Global Security / Offline Overlay */}
                <LockOverlay isOnline={isOnline} hasFetched={hasFetched} />

                <AnimatePresence mode="wait">
                    <motion.div
                        key={searchQuery ? 'search' : `${activeTab}-${activeSimSubTab}`}
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.15 }}
                    >
                        {activeTab === 'SIM' && (
                            <div className="flex flex-col gap-0">
                                {activeSimSubTab === 'ENGINE' && (
                                        <div className="flex flex-col gap-0">
                                            <ModeSelectorSection 
                                                config={config} 
                                                setConfig={setConfig} 
                                                isLocked={isSettingLocked('modeSelector')}
                                                minPlan={getLockInfo('modeSelector').min_plan}
                                                onUpgradeClick={() => setActiveTab('PLAN')}
                                            />
                                            
                                            {shouldShowSection(['RHYTHMIC', 'RHYTHMIC_ESCAPE']) && (
                                                <RhythmicModeSection 
                                                    config={config} 
                                                    setConfig={setConfig} 
                                                    onReset={onReset} 
                                                    isLocked={isSettingLocked('rhythmicEngine')}
                                                    minPlan={getLockInfo('rhythmicEngine').min_plan}
                                                    getLockInfo={getLockInfo}
                                                    onUpgradeClick={() => setActiveTab('PLAN')}
                                                />
                                            )}

                                            {shouldShowSection(['DRAWER']) && (
                                                <DrawerSettingsSection 
                                                    config={config} 
                                                    setConfig={setConfig} 
                                                    isLocked={isSettingLocked('drawer')}
                                                    minPlan={getLockInfo('drawer').min_plan}
                                                    onUpgradeClick={() => setActiveTab('PLAN')}
                                                />
                                            )}

                                            {shouldShowSection(['SPAWNER']) && (
                                                <SpawnerSettingsSection 
                                                    config={config} 
                                                    setConfig={setConfig} 
                                                    isLocked={isSettingLocked('spawner')}
                                                    minPlan={getLockInfo('spawner').min_plan}
                                                    onUpgradeClick={() => setActiveTab('PLAN')}
                                                />
                                            )}

                                            {shouldShowSection(['ESCAPE', 'RHYTHMIC_ESCAPE']) && (
                                                <EscapeSettingsSection 
                                                    config={config} 
                                                    setConfig={setConfig} 
                                                    isLocked={isSettingLocked('escape')}
                                                    minPlan={getLockInfo('escape').min_plan}
                                                    onUpgradeClick={() => setActiveTab('PLAN')}
                                                />
                                            )}

                                            {shouldShowSection(['SPACE_SPIRAL']) && (
                                                <SpaceSpiralSettingsSection 
                                                    config={config} 
                                                    setConfig={setConfig} 
                                                    isLocked={isSettingLocked('spaceSpiralStudio')}
                                                    minPlan={getLockInfo('spaceSpiralStudio').min_plan}
                                                    getLockInfo={getLockInfo}
                                                    onUpgradeClick={() => setActiveTab('PLAN')}
                                                />
                                            )}
                                        </div>
                                )}

                                {activeSimSubTab === 'WORLD' && (
                                    <div className="flex flex-col gap-0">
                                        <BackgroundSettingsSection 
                                            config={config} 
                                            setConfig={setConfig} 
                                            isLocked={isSettingLocked('background')}
                                            minPlan={getLockInfo('background').min_plan}
                                            getLockInfo={getLockInfo}
                                            onUpgradeClick={() => setActiveTab('PLAN')}
                                        />
                                        <HUDSettingsSection 
                                            config={config} 
                                            setConfig={setConfig} 
                                            isLocked={isSettingLocked('hud')}
                                            minPlan={getLockInfo('hud').min_plan}
                                            onUpgradeClick={() => setActiveTab('PLAN')}
                                        />
                                        <CameraSettingsSection 
                                            config={config} 
                                            setConfig={setConfig} 
                                            isLocked={isSettingLocked('camera')}
                                            minPlan={getLockInfo('camera').min_plan}
                                            onUpgradeClick={() => setActiveTab('PLAN')}
                                        />
                                    </div>
                                )}

                                {activeSimSubTab === 'OPTIMIZATION' && (
                                    <OptimizationSettingsSection 
                                        config={config} 
                                        setConfig={setConfig} 
                                        isLocked={isSettingLocked('optimization')}
                                        minPlan={getLockInfo('optimization').min_plan}
                                        onUpgradeClick={() => setActiveTab('PLAN')}
                                        isSettingLocked={isSettingLocked}
                                        getLockInfo={getLockInfo}
                                        ballPoolRef={ballPoolRef}
                                        trailCirclePoolRef={trailCirclePoolRef}
                                        collisionEventPoolRef={collisionEventPoolRef}
                                        blastEventPoolRef={blastEventPoolRef}
                                        workerRef={workerRef}
                                    />
                                )}
                            </div>
                        )}

                        {activeTab === 'PHYSICS' && (
                            <PhysicsTab 
                                config={config} 
                                setConfig={setConfig} 
                                getLockInfo={getLockInfo} 
                                onUpgradeClick={() => setActiveTab('PLAN')} 
                            />
                        )}

                        {activeTab === 'VISUALS' && (
                            <VisualsTab config={config} setConfig={setConfig} getLockInfo={getLockInfo} onUpgradeClick={() => setActiveTab('PLAN')} />
                        )}

                        {activeTab === 'AUDIO' && (
                            <AudioStudioTab 
                                config={config} setConfig={setConfig} 
                                onFileUpload={onFileUpload} isAnalyzing={isAnalyzing} analysisProgress={analysisProgress}
                                onBallSoundUpload={onBallSoundUpload} isUploadingBallSound={isUploadingBallSound}
                                getLockInfo={getLockInfo}
                                onUpgradeClick={() => setActiveTab('PLAN')}
                            />
                        )}

                        {activeTab === 'REC' && (
                            <StudioRecorderTab 
                                config={config} 
                                setConfig={setConfig} 
                                canvasRef={canvasRef} 
                                bgCanvasRef={bgCanvasRef} 
                                webglCanvasRef={webglCanvasRef}
                                sim2dCanvasRef={sim2dCanvasRef}
                                isLocked={isSettingLocked('STUDIO_RECORDER')}
                                minPlan={getLockInfo('STUDIO_RECORDER').min_plan}
                                onUpgradeClick={() => setActiveTab('PLAN')}
                            />
                        )}

                        {activeTab === 'DATA' && (
                            <StorageManager config={config} setConfig={setConfig} cloudSaves={cloudSaves} setCloudSaves={setCloudSaves} onReset={onReset} />
                        )}

                        {activeTab === 'PLAN' && (
                            <UpgradeTab config={config} />
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
      </motion.div>
    </div>
    </SettingsSearchContext.Provider>
    </ConfigContext.Provider>
  );
};
